import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getDetailEvent } from "../services/apiService";
import WebsiteLayout from "../layouts/WebsiteLayout";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorMessage from "../components/common/ErrorMessage";
import BackNavButton from "../components/common/BackNavButton";
import {
  getBottleServiceHeroImageUrl,
  isApiSuccess,
  pickAddress,
  pickImageUrl,
  pickRating,
  pickTitle,
  resolvePublicAssetUrl,
} from "../utils/clubDetailHelpers";
import { googleMapsEmbedUrl, googleMapsSearchUrl, pickCoordinates } from "../utils/eventDetailHelpers";
import "./BottleDetailPage.css";

function normalizeBottleGroups(event) {
  const raw =
    event?.bottleService ||
    event?.bottleServices ||
    event?.bottleMenu ||
    event?.menu ||
    event?.bottles ||
    event?.items ||
    [];

  const normalizeTitle = (value) =>
    String(value || "Bottle Service")
      .replace(/[_-]/g, " ")
      .replace(/\b\w/g, (m) => m.toUpperCase());

  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const groups = [];
    Object.entries(raw).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length) {
        groups.push({ title: normalizeTitle(key), items: value });
      }
    });
    if (groups.length) return groups;
  }

  if (!Array.isArray(raw)) return [];
  const grouped = {};
  raw.forEach((item) => {
    const key = item?.category || item?.type || item?.group || item?.section || item?.serviceType || "Bottle Service";
    const title = String(key || "Bottle Service");
    if (!grouped[title]) grouped[title] = [];
    grouped[title].push(item);
  });
  return Object.entries(grouped).map(([title, items]) => ({ title, items }));
}

function pickBottleName(item) {
  return item?.name || item?.title || item?.bottleName || item?.productName || item?.label || "Bottle";
}

function pickBottleImage(item) {
  const raw = item?.image || item?.imageUrl || item?.photo || item?.thumbnail || item?.icon || item?.picture || "";
  if (typeof raw === "string" && raw.trim()) return resolvePublicAssetUrl(raw.trim());
  return pickImageUrl(item || {});
}

export default function BottleDetailPage() {
  const { eventId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hostFromUrl = (searchParams.get("host") || "").trim();
  const fromEventUrl = searchParams.get("fromEvent") === "1";
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getDetailEvent(eventId);
        if (cancelled) return;
        if (!isApiSuccess(res?.status) || !res?.data) {
          setError(res?.message || "Bottle detail not found");
          setEvent(null);
          return;
        }
        setEvent(res.data);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Error");
          setEvent(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const title = pickTitle(event || {});
  const address = pickAddress(event || {});
  const about = event?.details || event?.description || event?.about || "";
  const thumb = getBottleServiceHeroImageUrl() || pickImageUrl(event || {});
  const rating = pickRating(event || {}) ?? 0;
  const orgName =
    event?.organizationName || event?.businessName || event?.hostId?.fullName || event?.userId?.fullName || "Organization";
  const orgRole = event?.organizationTeam || event?.hostRole || "Organization Team";
  const orgAvatar =
    event?.businessPicture ||
    event?.businessLogo ||
    event?.profilePicture ||
    event?.userId?.businessPicture ||
    event?.userId?.profilePicture ||
    event?.hostId?.businessPicture ||
    event?.hostId?.profilePicture ||
    pickImageUrl(event?.hostId || event?.userId || event || {}) ||
    "";
  const startDateRaw = event?.startDate || event?.eventStartDate || event?.date;
  const startDateLabel = startDateRaw
    ? new Date(startDateRaw).toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : "";
  const startTimeLabel = event?.openingTime || event?.startTime || "";
  const bottleGroups = useMemo(() => normalizeBottleGroups(event), [event]);
  const backTo = hostFromUrl ? `/club/${encodeURIComponent(hostFromUrl)}` : "/";
  const availabilityPath = (() => {
    const query = new URLSearchParams();
    if (hostFromUrl) query.set("host", hostFromUrl);
    if (fromEventUrl) query.set("fromEvent", "1");
    const qs = query.toString();
    return `/bottle/${encodeURIComponent(eventId)}/availability${qs ? `?${qs}` : ""}`;
  })();
  const coords = useMemo(() => pickCoordinates(event || {}), [event]);
  const mapSearchHref = useMemo(
    () => googleMapsSearchUrl(coords?.lat ?? null, coords?.lng ?? null, address),
    [coords, address]
  );
  const embedUrl = useMemo(() => (coords ? googleMapsEmbedUrl(coords.lat, coords.lng) : ""), [coords]);

  if (!eventId) {
    return (
      <WebsiteLayout>
        <ErrorMessage message="Missing bottle id." />
      </WebsiteLayout>
    );
  }
  if (loading) {
    return (
      <WebsiteLayout>
        <LoadingSpinner label="Loading bottle detail..." />
      </WebsiteLayout>
    );
  }
  if (error || !event) {
    return (
      <WebsiteLayout>
        <ErrorMessage message={error || "Not found"} />
      </WebsiteLayout>
    );
  }

  return (
    <WebsiteLayout>
      <div className="bottle-page">
        <div className="bottle-hero-shell">
          <div className="bottle-hero-wrap">
            {!fromEventUrl ? (
              <BackNavButton
                to={backTo}
                className="back-nav-btn--overlay"
                ariaLabel="Back"
              />
            ) : null}
            {thumb ? <img src={thumb} alt="" className="bottle-hero-image" /> : <div className="bottle-hero-fallback">Bottle</div>}
          </div>
        </div>

        <div className="bottle-content">
          <div className="bottle-inner">
            <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
              <span className="bottle-pill">Bottle Service</span>
              <span style={{ color: "#f5c542", fontWeight: 700 }}>★ {String(rating)}</span>
            </div>
            <h1 className="bottle-title">{title}</h1>
            <p className="bottle-meta-line">
              {address ? <span>📍 {address}</span> : null}
            </p>
            {startDateLabel || startTimeLabel ? (
              <p className="bottle-meta-line">
                ⏰ {`${startDateLabel}${startTimeLabel ? ` ${startTimeLabel}` : ""}`}
              </p>
            ) : null}

            <section className="bottle-section">
              <h2 className="bottle-section-title">Bottle Service</h2>
              <p className="bottle-about">Select date and bottle choices on the availability screen.</p>
              {bottleGroups.length ? (
                bottleGroups.map((group, idx) => (
                  <div key={`${group.title}-${idx}`} className="bottle-group-card">
                    <h3 className="bottle-group-title">{group.title}</h3>
                    <ul className="list-unstyled mb-0">
                      {(group.items || []).map((item, itemIdx) => (
                        <li key={`${group.title}-${itemIdx}`} className="bottle-item-row">
                          <div className="bottle-item-left">
                            <div
                              className="bottle-item-thumb"
                              style={{
                                backgroundImage: pickBottleImage(item) ? `url(${pickBottleImage(item)})` : undefined,
                              }}
                            />
                            <p className="bottle-item-name">{pickBottleName(item)}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))
              ) : (
                <p className="bottle-about">Bottle menu is not available.</p>
              )}
            </section>

            {about ? (
              <section className="bottle-section">
                <h2 className="bottle-section-title">About</h2>
                <p className="bottle-about">{about}</p>
              </section>
            ) : null}

            <section className="bottle-section">
              <h2 className="bottle-section-title">Organizations</h2>
              <div className="bottle-org-card">
                <div className="bottle-org-main">
                  <div className="bottle-org-avatar" style={{ backgroundImage: orgAvatar ? `url(${orgAvatar})` : undefined }} />
                  <div>
                    <p className="bottle-org-name">{orgName}</p>
                    <p className="bottle-org-role">{orgRole}</p>
                  </div>
                </div>
                <div className="bottle-org-actions">
                  {event?.phone ? <a href={`tel:${event.phone}`}>📞</a> : null}
                  {event?.email ? <a href={`mailto:${event.email}`}>✉</a> : null}
                </div>
              </div>
            </section>

            {address ? (
              <section className="bottle-section">
                <div className="d-flex justify-content-between align-items-center">
                  <h2 className="bottle-section-title mb-0">Address</h2>
                  {mapSearchHref ? (
                    <a href={mapSearchHref} target="_blank" rel="noopener noreferrer" className="bottle-map-link">
                      View on Map
                    </a>
                  ) : null}
                </div>
                <p className="bottle-meta-line mt-2">📍 {address}</p>
                {embedUrl ? (
                  <div className="bottle-map-frame">
                    <iframe
                      title="Bottle venue map"
                      src={embedUrl}
                      className="bottle-map-iframe"
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      allowFullScreen
                    />
                  </div>
                ) : null}
              </section>
            ) : null}
          </div>
        </div>
        <div className="bottle-bottom-action">
          <button type="button" className="bottle-next-btn" onClick={() => navigate(availabilityPath)}>
            Next
          </button>
        </div>
      </div>
    </WebsiteLayout>
  );
}
